import { ReactNode } from 'react';
import { Key } from '@react-types/shared';
import { Controller, useFormContext } from 'react-hook-form';
import { Chip, SelectItem, TmdxSelect } from '@ds';
import { AdminFormRow } from '@ui';
import { EmployeeFormValues, SelectItemType } from '@/types';

type MultiSelectFieldName =
  | 'organType'
  | 'phoneSupportOrganSpecializations'
  | 'rsScheduleManagerOrgans'
  | 'trialProgramIds'
  | 'organManagementSpecialistSpecializations'
  | 'roles';

type MultiSelectWithChipsRowProps = {
  name: MultiSelectFieldName;
  title: string;
  ariaLabel: string;
  placeholder: string;
  items: SelectItemType[];
  isDisabled?: boolean;
  isRequired?: boolean;
  disabledKeys?: Iterable<Key>;
  helperText?: ReactNode;
  onSelectionChangeExtra?: (keys: Key[]) => void;
};

// TODO move this to some shared folder if it will be used by other forms
export const MultiSelectWithChipsRow = ({
  name,
  title,
  ariaLabel,
  placeholder,
  items,
  isDisabled,
  isRequired,
  disabledKeys,
  helperText,
  onSelectionChangeExtra,
}: MultiSelectWithChipsRowProps) => {
  const { control } = useFormContext<EmployeeFormValues>();

  return (
    <AdminFormRow title={title} isRequired={isRequired}>
      <Controller
        name={name}
        control={control}
        render={({ field: { value, ref, onChange, ...field }, fieldState: { invalid, error } }) => (
          <TmdxSelect
            items={items}
            variant="bordered"
            isMultiline
            selectionMode="multiple"
            disabledKeys={disabledKeys}
            placeholder={placeholder}
            labelPlacement="outside"
            aria-label={ariaLabel}
            isInvalid={invalid}
            isDisabled={isDisabled}
            errorMessage={error?.message}
            description={helperText}
            selectedKeys={value ?? []}
            onSelectionChange={(keys) => {
              const arr = Array.from(keys);
              onChange(arr);
              onSelectionChangeExtra?.(arr);
            }}
            {...field}
            renderValue={(selectedItems) => {
              const optionList = selectedItems.map((item) => item.data) as SelectItemType[];
              return (
                <div className="flex flex-wrap gap-2">
                  {optionList.map((item) => (
                    <Chip key={item.value}>{item.name}</Chip>
                  ))}
                </div>
              );
            }}
          >
            {(item) => {
              const selectItem = item as SelectItemType;
              return (
                <SelectItem textValue={selectItem.name} key={selectItem.value}>
                  <div className="flex gap-2 items-center">
                    <div className="flex flex-row">
                      <span className="text-small">{selectItem.name}</span>
                    </div>
                  </div>
                </SelectItem>
              );
            }}
          </TmdxSelect>
        )}
      />
    </AdminFormRow>
  );
};
